import { SxProps, Theme } from '@mui/material';
import { brandColors } from '../../../theme/mixins';

export const headerBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 2,
    mb: 3,
};

export const titleStyles: SxProps<Theme> = {
    fontWeight: 700,
    color: brandColors.foreground,
};

export const subtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

export const stepperStyles: SxProps<Theme> = {
    mb: 4,
};

export const formWrapperStyles: SxProps<Theme> = {
    p: { xs: 2, sm: 3 },
    borderRadius: 2,
};

export const sectionTitleStyles: SxProps<Theme> = {
    fontWeight: 600,
    color: brandColors.foreground,
    mt: 3,
    mb: 1.5,
};

/**
 * The identity block. Two columns on a laptop, one on a phone -- a therapist filling
 * this in at the desk is usually on a tablet held upright.
 */
export const fieldGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
    gap: 2,
};

/** Shared so the child's age row below can sit in the same columns without restating them. */
const narrowGrid = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
    gap: 2,
} as const;

export const narrowGridStyles: SxProps<Theme> = narrowGrid;

/**
 * The child's age, on its own line under the capacity radios and one column wide -- a
 * two-digit answer given the width of a two-digit field rather than the width of the page,
 * which is the same treatment the ID series and number get.
 *
 * The row exists because a `TextField` *is* a `FormControl`, and MUI renders those
 * `inline-flex`: left to itself the field flows up beside the radio group and sits on top
 * of the "Signing as" label.
 */
export const childAgeRowStyles: SxProps<Theme> = {
    ...narrowGrid,
    mt: 2,
    mb: 1,
};

/** The assessment fields sit full width: they are prose, not data entry. */
export const stackedFieldsStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};

export const assessmentLayoutStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 260px' },
    gap: 3,
    alignItems: 'start',
};

export const diagramBoxStyles: SxProps<Theme> = {
    position: { md: 'sticky' },
    top: { md: 16 },
    textAlign: 'center',
};



export const hintStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.85rem',
    mt: -0.5,
    mb: 1,
};

export const consentIntroStyles: SxProps<Theme> = {
    color: 'text.secondary',
    mb: 1,
};

/**
 * Scrollable rather than eighteen items pushing the consent tick-box off the screen.
 * Bounded height is what keeps "read this list" and "confirm you read it" visible at
 * the same time.
 */
export const contraindicationsBoxStyles: SxProps<Theme> = {
    maxHeight: 280,
    overflowY: 'auto',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 2,
    mb: 2,
};

export const contraindicationItemStyles: SxProps<Theme> = {
    display: 'list-item',
    listStylePosition: 'outside',
    ml: 2,
    py: 0.25,
    fontSize: '0.9rem',
};

export const declarationStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.9rem',
    mb: 2,
};

export const gdprParagraphStyles: SxProps<Theme> = {
    mb: 2,
    textAlign: 'justify',
    fontSize: '0.9rem',
};

export const consentCheckboxStyles: SxProps<Theme> = {
    mt: 1,
};

export const navigationButtonsBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    mt: 4,
};

export const alertStyles: SxProps<Theme> = {
    mb: 2,
};

/** While a follow-up's patient and their last sheet are being fetched. */
export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 8,
};
